import { useRef, useState, useCallback, useEffect } from 'react';
import './Canvas.css';

const COLORS = ['#f3f4f6', '#7b61ff', '#b0f3fe', '#f472b6', '#b9f4e4', '#eab308', '#f97316'];
const SIZES = [2, 4, 8, 16];

function drawInitialDiagram(ctx, drawing, canvasWidth, canvasHeight) {
  if (!drawing || !Array.isArray(drawing)) return;
  for (const item of drawing) {
    ctx.save();
    if (item.type === 'box') {
      ctx.strokeStyle = item.color || '#ffffff';
      ctx.lineWidth = 2;
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      const r = 12;
      const { x, y, w, h } = item;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      if (item.label) {
        ctx.fillStyle = '#f3f4f6';
        ctx.font = '13px Gilroy, Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const lines = item.label.split('\n');
        lines.forEach((line, i) => {
          ctx.fillText(line, x + w / 2, y + h / 2 + (i - (lines.length - 1) / 2) * 16);
        });
      }
    } else if (item.type === 'arrow') {
      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(item.from[0], item.from[1]);
      ctx.lineTo(item.to[0], item.to[1]);
      ctx.stroke();
      // Arrowhead
      const angle = Math.atan2(item.to[1] - item.from[1], item.to[0] - item.from[0]);
      ctx.beginPath();
      ctx.moveTo(item.to[0], item.to[1]);
      ctx.lineTo(item.to[0] - 8 * Math.cos(angle - 0.4), item.to[1] - 8 * Math.sin(angle - 0.4));
      ctx.moveTo(item.to[0], item.to[1]);
      ctx.lineTo(item.to[0] - 8 * Math.cos(angle + 0.4), item.to[1] - 8 * Math.sin(angle + 0.4));
      ctx.stroke();
    } else if (item.type === 'label') {
      ctx.fillStyle = item.color || '#8b92a5';
      ctx.font = '600 10px Gilroy, Inter, sans-serif';
      ctx.letterSpacing = '0.5px';
      ctx.textAlign = 'left';
      ctx.fillText(item.text.toUpperCase(), item.x, item.y);
    }
    ctx.restore();
  }
}

export default function Canvas({ initialDrawing, onClose }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState(COLORS[0]);
  const [size] = useState(SIZES[1]);
  const [tool, setTool] = useState('pen');
  const lastPos = useRef(null);

  // Keep the canvas pixel buffer sized to its display box. Without this the
  // buffer is sized once at mount — mid-transition when the right panel is
  // animating in — and then later mouse events use rect coords that no
  // longer match the buffer, so strokes appear offset/scaled.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const paintBackground = () => {
      ctx.fillStyle = '#111216';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (initialDrawing) {
        drawInitialDiagram(ctx, initialDrawing, canvas.width, canvas.height);
      }
    };

    const resync = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      if (!w || !h) return;
      if (canvas.width === w && canvas.height === h) return;
      // Preserve current pixels across the resize
      let snapshot = null;
      if (canvas.width && canvas.height) {
        try { snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height); }
        catch (e) { snapshot = null; }
      }
      canvas.width = w;
      canvas.height = h;
      paintBackground();
      if (snapshot) {
        try { ctx.putImageData(snapshot, 0, 0); } catch (e) { /* ignore */ }
      }
    };

    resync();

    const ro = new ResizeObserver(resync);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [initialDrawing]);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPos.current = getPos(e);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = tool === 'eraser' ? '#111216' : color;
    ctx.lineWidth = tool === 'eraser' ? size * 4 : size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  }, [isDrawing, color, size, tool, getPos]);

  const stopDraw = useCallback(() => {
    setIsDrawing(false);
    lastPos.current = null;
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#111216';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  return (
    <div className="widget-card">
      <div className="widget-card-header">
        <span className="widget-card-title">Whiteboard</span>
        <button className="widget-close-btn" onClick={onClose}>&times;</button>
      </div>
      <div className="canvas-toolbar">
        <div className="toolbar-group">
          <button className={`tool-btn ${tool === 'pen' ? 'active' : ''}`} onClick={() => setTool('pen')}>Pen</button>
          <button className={`tool-btn ${tool === 'eraser' ? 'active' : ''}`} onClick={() => setTool('eraser')}>Eraser</button>
        </div>
        <div className="toolbar-group colors">
          {COLORS.map((c) => (
            <button
              key={c}
              className={`color-btn ${color === c ? 'active' : ''}`}
              style={{ background: c }}
              onClick={() => { setColor(c); setTool('pen'); }}
            />
          ))}
        </div>
        <button className="tool-btn clear-btn" onClick={clearCanvas}>Clear</button>
      </div>
      <div className="canvas-area">
        <canvas
          ref={canvasRef}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
      </div>
    </div>
  );
}
